import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { questionnaireService } from '../services/questionnaireService';
import { questionService } from '../services/questionService';
import { versionService } from '../services/versionService';
import { pushService } from '../services/pushService';
import { Questionnaire, Question } from '../types';
import {
  PlusIcon,
  ArrowLeftIcon,
  EditIcon,
  TrashIcon,
  CopyIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  FileTextIcon,
  EyeIcon,
  ClipboardIcon,
  DownloadIcon
} from '../components/Icons';
import DisplayLogicManager from '../components/DisplayLogicManager';
import VersionHistory from '../components/VersionHistory';
import ConfirmDialog from '../components/ConfirmDialog';
import ConsentConfigTab from '../components/ConsentConfigTab';
import PaginationConfigTab from '../components/PaginationConfigTab';
import SaveToBankModal from '../components/SaveToBankModal';
import ImportFromBankModal from '../components/ImportFromBankModal';
import ScoreEditModal from '../components/ScoreEditModal';
import { useToast } from '../context/ToastContext';

const QuestionnaireDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [questionnaire, setQuestionnaire] = useState<Questionnaire | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'questions' | 'logic' | 'versions' | 'indicators' | 'report' | 'consent' | 'pagination' | 'pushes'>('questions');
  const [availableVersions, setAvailableVersions] = useState<Questionnaire[]>([]);
  const [showVersionSelector, setShowVersionSelector] = useState(false);
  const [showMarkVersionModal, setShowMarkVersionModal] = useState(false);
  const [pushes, setPushes] = useState<any[]>([]);
  const [loadingPushes, setLoadingPushes] = useState(false);
  const [showPushDetailModal, setShowPushDetailModal] = useState(false);
  const [selectedPush, setSelectedPush] = useState<any>(null);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [saveToBankModal, setSaveToBankModal] = useState<{ show: boolean; questionId: number | null }>({ show: false, questionId: null });
  const [scoreModalQuestion, setScoreModalQuestion] = useState<Question | null>(null);
  const [showImportFromBank, setShowImportFromBank] = useState(false);
  // 改问卷名字
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [savingTitle, setSavingTitle] = useState(false);

  useEffect(() => {
    if (id) {
      fetchData();
      fetchVersions();
    }
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [questionnaireData, questionsData] = await Promise.all([
        questionnaireService.getQuestionnaire(Number(id)),
        questionService.getQuestions(Number(id)),
      ]);
      setQuestionnaire(questionnaireData);
      setQuestions(questionsData);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVersions = async () => {
    try {
      const versions = await versionService.getVersionHistory(Number(id));
      setAvailableVersions(versions);
    } catch (error) {
      console.error('Failed to fetch versions:', error);
    }
  };

  const fetchPushes = async () => {
    if (!id) return;
    try {
      setLoadingPushes(true);
      const data = await pushService.getQuestionnairePushes(Number(id));
      setPushes(data);
    } catch (error) {
      console.error('Failed to fetch pushes:', error);
    } finally {
      setLoadingPushes(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'pushes') {
      fetchPushes();
    }
  }, [activeTab]);

  const handleViewPushDetail = (push: any) => {
    setSelectedPush(push);
    setShowPushDetailModal(true);
  };

  const handleSwitchVersion = (versionId: number) => {
    navigate(`/questionnaires/${versionId}`);
    setShowVersionSelector(false);
  };

  // 点击外部关闭版本选择器
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showVersionSelector) {
        const target = event.target as HTMLElement;
        if (!target.closest('.version-selector')) {
          setShowVersionSelector(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showVersionSelector]);

  const handleDelete = async (questionId: number) => {
    if (!window.confirm('确定要删除这个问题吗？')) return;

    try {
      await questionService.deleteQuestion(Number(id), questionId);
      fetchData();
      showToast('问题删除成功', 'success');
    } catch (error) {
      console.error('Failed to delete question:', error);
      showToast('删除失败，请重试', 'error');
    }
  };

  const handleCopy = async (questionId: number) => {
    try {
      await questionService.copyQuestion(Number(id), questionId);
      fetchData();
      showToast('问题复制成功', 'success');
    } catch (error) {
      console.error('Failed to copy question:', error);
      showToast('复制失败，请重试', 'error');
    }
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0) return;
    const newQuestions = [...questions];
    [newQuestions[index - 1], newQuestions[index]] = [newQuestions[index], newQuestions[index - 1]];

    try {
      await questionService.reorderQuestions(
        Number(id),
        newQuestions.map(q => q.id)
      );
      setQuestions(newQuestions);
    } catch (error) {
      console.error('Failed to reorder questions:', error);
      showToast('排序失败，请重试', 'error');
    }
  };

  const handleMoveDown = async (index: number) => {
    if (index === questions.length - 1) return;
    const newQuestions = [...questions];
    [newQuestions[index], newQuestions[index + 1]] = [newQuestions[index + 1], newQuestions[index]];

    try {
      await questionService.reorderQuestions(
        Number(id),
        newQuestions.map(q => q.id)
      );
      setQuestions(newQuestions);
    } catch (error) {
      console.error('Failed to reorder questions:', error);
      showToast('排序失败，请重试', 'error');
    }
  };

  const handleStartEditTitle = () => {
    if (!questionnaire) return;
    setTitleDraft(questionnaire.title);
    setEditingTitle(true);
  };

  const handleSaveTitle = async () => {
    if (!questionnaire) return;
    const trimmed = titleDraft.trim();
    if (!trimmed) {
      showToast('问卷标题不能为空', 'error');
      return;
    }
    if (trimmed === questionnaire.title) {
      setEditingTitle(false);
      return;
    }
    try {
      setSavingTitle(true);
      await questionnaireService.updateQuestionnaire(Number(id), { title: trimmed });
      setEditingTitle(false);
      fetchData();
      showToast('问卷标题已更新', 'success');
    } catch (error: any) {
      console.error('Failed to rename questionnaire:', error);
      showToast(error?.response?.data?.message || '改名失败，请重试', 'error');
    } finally {
      setSavingTitle(false);
    }
  };

  const handleCancelEditTitle = () => {
    if (savingTitle) return;
    setEditingTitle(false);
    setTitleDraft('');
  };

  const handleAddPageBreak = async (questionId: number) => {
    try {
      await questionService.setPageBreak(Number(id), questionId, true);
      // Auto-enable pagination if not already enabled
      if (questionnaire && !questionnaire.paginationEnabled) {
        await questionnaireService.updateQuestionnaire(Number(id), {
          title: questionnaire.title,
          description: questionnaire.description,
          paginationEnabled: true,
        });
      }
      fetchData();
      showToast('分页符已插入', 'success');
    } catch (error) {
      console.error('Failed to add page break:', error);
      showToast('操作失败，请重试', 'error');
    }
  };

  const handleRemovePageBreak = async (questionId: number) => {
    try {
      await questionService.setPageBreak(Number(id), questionId, false);
      fetchData();
      showToast('分页符已移除', 'success');
    } catch (error) {
      console.error('Failed to remove page break:', error);
      showToast('操作失败，请重试', 'error');
    }
  };

  const handleMarkVersion = () => {
    setShowMarkVersionModal(true);
  };

  const confirmMarkVersion = async () => {
    try {
      const result = await questionnaireService.markVersion(Number(id));
      setShowMarkVersionModal(false);
      // 跳转到新创建的版本
      navigate(`/questionnaires/${result.id}`);
      showToast('版本标记成功', 'success');
    } catch (error) {
      console.error('Failed to mark version:', error);
      showToast('标记版本失败，请重试', 'error');
    }
  };

  const handlePublish = () => {
    setShowPublishConfirm(true);
  };

  const confirmPublish = async () => {
    setShowPublishConfirm(false);
    try {
      await questionnaireService.publishQuestionnaire(Number(id));
      fetchData();
      showToast('问卷发布成功', 'success');
    } catch (error: any) {
      console.error('Failed to publish questionnaire:', error);
      showToast(error.response?.data?.message || '发布失败', 'error');
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      DRAFT: 'bg-surface-container-high text-on-surface',
      PUBLISHED: 'bg-success-100 text-success-700',
      ARCHIVED: 'bg-warning-100 text-warning-700',
    };
    return colors[status] || 'bg-surface-container-high text-on-surface';
  };

  const getQuestionTypeIcon = (type: string) => {
    const icons: Record<string, string> = {
      SINGLE_CHOICE: '◉',
      MULTIPLE_CHOICE: '☑',
      TEXT: '⌨',
      SLIDER: '─',
      MATRIX: '▦',
      DESCRIPTION: '📄',
      FILL_IN_BLANK: '___',
      DYNAMIC_FILL_IN_BLANK: '＋___',
    };
    return icons[type] || '?';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!questionnaire) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold font-headline text-on-surface mb-2">问卷不存在</h2>
          <button
            onClick={() => navigate('/questionnaires')}
            className="text-primary-600 hover:text-primary-700"
          >
            返回列表
          </button>
        </div>
      </div>
    );
  }

  const isDraft = questionnaire.status === 'DRAFT';

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/questionnaires')}
          className="flex items-center text-on-surface-variant hover:text-on-surface mb-4 font-body"
        >
          <ArrowLeftIcon size={20} className="mr-2" />
          返回列表
        </button>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              {editingTitle ? (
                <div className="flex items-center gap-2 flex-1">
                  <input
                    autoFocus
                    type="text"
                    value={titleDraft}
                    onChange={(e) => setTitleDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveTitle();
                      if (e.key === 'Escape') handleCancelEditTitle();
                    }}
                    maxLength={200}
                    disabled={savingTitle}
                    className="text-2xl font-bold font-headline text-on-surface bg-white border-2 border-primary rounded-lg px-3 py-1 focus:outline-none focus:ring-2 focus:ring-primary-200 min-w-[300px]"
                  />
                  <button
                    onClick={handleSaveTitle}
                    disabled={savingTitle || !titleDraft.trim()}
                    className="px-3 py-1.5 bg-primary text-on-primary rounded-lg text-sm font-medium hover:bg-primary-dim disabled:opacity-50"
                  >
                    {savingTitle ? '保存中…' : '保存'}
                  </button>
                  <button
                    onClick={handleCancelEditTitle}
                    disabled={savingTitle}
                    className="px-3 py-1.5 bg-surface-container-high text-on-surface rounded-lg text-sm hover:bg-surface-container-low disabled:opacity-50"
                  >
                    取消
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2 group">
                  <h1 className="text-2xl font-bold font-headline text-on-surface">{questionnaire.title}</h1>
                  <button
                    onClick={handleStartEditTitle}
                    title="重命名问卷"
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-lg text-on-surface-variant hover:text-primary hover:bg-surface-container-high"
                  >
                    <EditIcon size={18} />
                  </button>
                </div>
              )}
              <span className={`px-2 py-1 rounded-xl text-xs font-label font-medium ${getStatusColor(questionnaire.status)}`}>
                {questionnaire.statusDisplayName}
              </span>
              {questionnaire.version && (
                <span className="px-2 py-1 bg-surface-container-high text-on-surface-variant rounded-xl text-xs font-label font-medium">
                  {questionnaire.version}
                </span>
              )}

              {/* 最新版本标识 */}
              {availableVersions.length > 0 &&
               questionnaire.id === availableVersions[availableVersions.length - 1].id &&
               questionnaire.status !== 'ARCHIVED' && (
                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-xl text-xs font-label font-medium">
                  最新版本
                </span>
              )}

              {/* 版本切换按钮 */}
              {availableVersions.length > 1 && (
                <div className="relative version-selector">
                  <button
                    onClick={() => setShowVersionSelector(!showVersionSelector)}
                    className="px-3 py-1 text-xs font-label bg-surface-container-high text-on-surface rounded-xl hover:bg-surface-container-low flex items-center gap-1"
                  >
                    切换版本
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {showVersionSelector && (
                    <div className="absolute top-full mt-1 right-0 bg-surface-container-lowest rounded-2xl shadow-soft z-10 min-w-[220px]">
                      {availableVersions.map((version) => (
                        <button
                          key={version.id}
                          onClick={() => handleSwitchVersion(version.id)}
                          className={`w-full text-left px-4 py-2 hover:bg-surface-container-low flex items-center justify-between first:rounded-t-2xl last:rounded-b-2xl font-body ${
                            version.id === questionnaire.id ? 'bg-primary-50 text-primary-700' : 'text-on-surface'
                          }`}
                        >
                          <span className="flex items-center gap-2">
                            <span>{version.version || '未标记版本'}</span>
                            <span className="text-xs font-label text-on-surface-variant">{version.statusDisplayName}</span>
                          </span>
                          {version.id === questionnaire.id && (
                            <span className="text-xs font-label">当前查看</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            {questionnaire.description && (
              <p className="text-on-surface-variant font-body mb-2">{questionnaire.description}</p>
            )}
            <div className="flex items-center gap-4 text-sm font-label text-on-surface-variant">
              <span>问题数：{questionnaire.questionCount}</span>
              <span>创建者：{questionnaire.createdByUsername}</span>
              <span>创建时间：{new Date(questionnaire.createdAt).toLocaleDateString()}</span>
            </div>
          </div>
          <div className="flex gap-3">
            {questionnaire.status === 'PUBLISHED' && (
              <button
                onClick={() => navigate(`/questionnaires/${id}/statistics`)}
                className="flex items-center gap-2 px-5 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 font-label font-medium shadow-sm"
                title="查看指标统计 / 大报告 / 答题结果"
              >
                <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>insights</span>
                统计分析
              </button>
            )}
            <button
              onClick={() => navigate(`/questionnaires/${id}/preview`)}
              className="flex items-center gap-2 px-4 py-2 bg-surface-container-high text-on-surface rounded-xl hover:bg-surface-container-low font-label"
            >
              <EyeIcon size={18} />
              预览
            </button>
            {isDraft && (
              <>
                <button
                  onClick={handleMarkVersion}
                  className="px-4 py-2 bg-surface-container-high text-on-surface rounded-xl hover:bg-surface-container-low font-label"
                >
                  标记版本
                </button>
                <button
                  onClick={handlePublish}
                  className="px-4 py-2 gradient-primary text-on-primary rounded-xl shadow-teal font-label"
                >
                  发布问卷
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* 标签页导航 */}
      <div className="mb-6 border-b border-surface-container-high">
        <div className="flex gap-5 overflow-x-auto">
          <button
            onClick={() => setActiveTab('questions')}
            className={`pb-3 px-1 border-b-2 font-label font-medium text-sm transition-colors flex items-center gap-1.5 ${
              activeTab === 'questions'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-on-surface-variant hover:text-on-surface'
            }`}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>list_alt</span>
            问题列表
          </button>
          <button
            onClick={() => setActiveTab('logic')}
            className={`pb-3 px-1 border-b-2 font-label font-medium text-sm transition-colors flex items-center gap-1.5 ${
              activeTab === 'logic'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-on-surface-variant hover:text-on-surface'
            }`}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>account_tree</span>
            跳转逻辑
            {questions.some(q => q.displayLogic) && (
              <span className="ml-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs">
                {questions.filter(q => q.displayLogic).length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('versions')}
            className={`pb-3 px-1 border-b-2 font-label font-medium text-sm transition-colors flex items-center gap-1.5 ${
              activeTab === 'versions'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-on-surface-variant hover:text-on-surface'
            }`}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>history</span>
            版本历史
          </button>
          <button
            onClick={() => setActiveTab('indicators')}
            className={`pb-3 px-1 border-b-2 font-label font-medium text-sm transition-colors flex items-center gap-1.5 ${
              activeTab === 'indicators'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-on-surface-variant hover:text-on-surface'
            }`}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>tune</span>
            指标配置
          </button>
          <button
            onClick={() => setActiveTab('report')}
            className={`pb-3 px-1 border-b-2 font-label font-medium text-sm transition-colors flex items-center gap-1.5 ${
              activeTab === 'report'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-on-surface-variant hover:text-on-surface'
            }`}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>description</span>
            大报告
          </button>
          <button
            onClick={() => setActiveTab('consent')}
            className={`pb-3 px-1 border-b-2 font-label font-medium text-sm transition-colors flex items-center gap-1.5 ${
              activeTab === 'consent'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-on-surface-variant hover:text-on-surface'
            }`}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>verified_user</span>
            知情同意书
            {questionnaire.consentRequired && (
              <span className="ml-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs">
                已启用
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('pagination')}
            className={`pb-3 px-1 border-b-2 font-label font-medium text-sm transition-colors flex items-center gap-1.5 ${
              activeTab === 'pagination'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-on-surface-variant hover:text-on-surface'
            }`}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>display_settings</span>
            显示设置
          </button>
          {questionnaire.status === 'PUBLISHED' && (
            <button
              onClick={() => setActiveTab('pushes')}
              className={`pb-3 px-1 border-b-2 font-label font-medium text-sm transition-colors flex items-center gap-1.5 ${
                activeTab === 'pushes'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-on-surface-variant hover:text-on-surface'
              }`}
            >
              <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>send</span>
              推送管理
            </button>
          )}
        </div>
      </div>

      {/* 问题列表标签页 */}
      {activeTab === 'questions' && (
        <>
          {questions.length === 0 ? (
        <div className="text-center py-12 bg-surface-container-lowest rounded-3xl shadow-soft">
          <FileTextIcon className="mx-auto mb-4 text-on-surface-variant" size={48} />
          <h3 className="text-lg font-medium font-headline text-on-surface mb-2">暂无问题</h3>
          <p className="text-on-surface-variant font-body mb-4">开始创建您的第一个问题</p>
          {isDraft && (
            <button
              onClick={() => navigate(`/questionnaires/${id}/questions/new/edit`)}
              className="inline-flex items-center gap-2 px-4 py-2 gradient-primary text-on-primary rounded-xl shadow-teal font-label"
            >
              <PlusIcon size={20} />
              添加问题
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold font-headline text-on-surface">问题列表</h2>
            <div className="flex items-center gap-3">
              {isDraft && (
                <>
                  <button
                    onClick={() => setShowImportFromBank(true)}
                    className="flex items-center gap-2 px-4 py-2 border border-primary-600 text-primary-600 rounded-xl hover:bg-primary-50 font-label"
                  >
                    <DownloadIcon size={20} />
                    从题库导入
                  </button>
                  <button
                    onClick={() => navigate(`/questionnaires/${id}/questions/new/edit`)}
                    className="flex items-center gap-2 px-4 py-2 gradient-primary text-on-primary rounded-xl shadow-teal font-label"
                  >
                    <PlusIcon size={20} />
                    添加问题
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="space-y-4">
            {questions.map((question, index) => (
              <React.Fragment key={question.id}>
              {/* Page break indicator */}
              {index > 0 && question.pageBreakBefore && (
                <div className="relative flex items-center py-1">
                  <div className="flex-1 border-t-2 border-dashed border-primary-400"></div>
                  <span className="mx-3 px-3 py-1 bg-primary-50 text-primary-700 text-xs font-label font-medium rounded-full border border-primary-200 flex items-center gap-1.5">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                    分页符
                    {isDraft && (
                      <button
                        onClick={() => handleRemovePageBreak(question.id)}
                        className="ml-1 text-primary-500 hover:text-danger-600 transition-colors"
                        title="移除分页符"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    )}
                  </span>
                  <div className="flex-1 border-t-2 border-dashed border-primary-400"></div>
                </div>
              )}
              {/* Insert page break button (between questions, when no page break exists) */}
              {isDraft && index > 0 && !question.pageBreakBefore && (
                <div className="relative flex items-center py-1">
                  <div className="flex-1 border-t border-dashed border-surface-container-high"></div>
                  <button
                    onClick={() => handleAddPageBreak(question.id)}
                    className="mx-3 px-3 py-1 text-xs font-label text-on-surface-variant hover:text-primary-600 hover:bg-primary-50 rounded-full border border-surface-container-high hover:border-primary-300 transition-all"
                    title="插入分页符"
                  >
                    + 插入分页符
                  </button>
                  <div className="flex-1 border-t border-dashed border-surface-container-high"></div>
                </div>
              )}
              <div
                className="bg-surface-container-lowest p-6 rounded-2xl shadow-soft hover:shadow-soft-md transition-shadow"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center font-medium text-sm">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-medium">{getQuestionTypeIcon(question.type)}</span>
                        {question.type === 'DESCRIPTION' ? (
                          <h3 className="text-lg font-medium font-headline text-on-surface-variant italic">说明文本</h3>
                        ) : (
                          <>
                            <h3 className="text-lg font-medium font-headline text-on-surface">{question.title}</h3>
                            {question.required && (
                              <span className="text-danger-600 text-sm font-label">*必填</span>
                            )}
                          </>
                        )}
                        {question.questionNumber && (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-xl text-xs font-label font-medium">
                            题号: {question.questionNumber}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-1 bg-surface-container-high text-on-surface-variant rounded-xl text-xs font-label">
                          {question.typeDisplayName}
                        </span>
                      </div>
                    </div>

                    {question.imageUrl && (
                      <div className="mb-3">
                        <img
                          src={question.imageUrl}
                          alt="Question"
                          className="max-w-xs rounded-2xl shadow-soft"
                        />
                      </div>
                    )}

                    {(question.type === 'SINGLE_CHOICE' || question.type === 'MULTIPLE_CHOICE') && (
                      <div className="mt-3 space-y-2">
                        {question.options.map((option, optIndex) => (
                          <div key={option.id} className="flex items-center gap-3 text-sm font-body text-on-surface">
                            <span className="text-on-surface-variant">{String.fromCharCode(65 + optIndex)}.</span>
                            <span>{option.content}</span>
                            {option.score !== null && option.score !== undefined && (
                              <span className="text-xs font-label text-on-surface-variant">({option.score}分)</span>
                            )}
                            {option.isOtherOption && (
                              <span className="text-xs font-label text-primary-600">(其他选项)</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {question.type === 'DESCRIPTION' && question.settings && (
                      <div className="mt-2 text-sm font-body text-on-surface-variant bg-blue-50 rounded-xl p-3 line-clamp-3" dangerouslySetInnerHTML={{ __html: question.settings }} />
                    )}

                    {question.type === 'MATRIX' && question.settings && (() => {
                      try {
                        const config = JSON.parse(question.settings);
                        const rows: string[] = config.rows || [];
                        const columns: string[] = config.columns || [];
                        const scores: Record<string, Record<string, number>> = config.scores || {};
                        const hasScores = Object.keys(scores).length > 0;
                        return (
                          <div className="mt-3 overflow-x-auto">
                            <table className="border-collapse text-sm font-body">
                              <thead>
                                <tr>
                                  <th className="p-2 border border-surface-container-high bg-surface-container-low min-w-[80px]"></th>
                                  {columns.map((col: string) => (
                                    <th key={col} className="p-2 border border-surface-container-high bg-surface-container-low text-center text-on-surface-variant font-medium min-w-[60px]">
                                      {col}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {rows.map((row: string) => (
                                  <tr key={row}>
                                    <td className="p-2 border border-surface-container-high bg-surface-container-low font-medium text-on-surface">{row}</td>
                                    {columns.map((col: string) => {
                                      const cellScore = hasScores && scores[row] ? scores[row][col] : undefined;
                                      return (
                                        <td key={col} className="p-2 border border-surface-container-high text-center">
                                          <div className="flex flex-col items-center gap-0.5">
                                            <div className="w-4 h-4 rounded-full border-2 border-surface-container-high"></div>
                                            {cellScore !== undefined && (
                                              <span className="text-xs font-label text-on-surface-variant">{cellScore}分</span>
                                            )}
                                          </div>
                                        </td>
                                      );
                                    })}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        );
                      } catch {
                        return null;
                      }
                    })()}

                    {question.type === 'SLIDER' && question.settings && (() => {
                      try {
                        const config = JSON.parse(question.settings);
                        return (
                          <div className="mt-3 flex items-center gap-3 text-sm font-body text-on-surface-variant">
                            <span className="font-medium">{config.min ?? 0}</span>
                            <div className="flex-1 h-2 bg-surface-container-high rounded-full max-w-[200px]">
                              <div className="h-2 bg-primary-400 rounded-full w-1/2"></div>
                            </div>
                            <span className="font-medium">{config.max ?? 100}</span>
                            {config.step && config.step !== 1 && (
                              <span className="text-xs font-label text-on-surface-variant">(步长: {config.step})</span>
                            )}
                          </div>
                        );
                      } catch {
                        return null;
                      }
                    })()}

                    {(question.type === 'FILL_IN_BLANK' || question.type === 'DYNAMIC_FILL_IN_BLANK') && question.settings && (() => {
                      try {
                        const config = JSON.parse(question.settings);
                        return (
                          <div className="mt-3 space-y-2">
                            {(config.fields || []).map((field: any, idx: number) => (
                              <div key={idx} className="flex items-center gap-2 text-sm font-body text-on-surface">
                                {field.label && <span className="font-medium">{field.label}:</span>}
                                <span className="text-on-surface-variant border-b border-surface-container-high px-4">__________</span>
                                {field.suffix && <span className="text-on-surface-variant">{field.suffix}</span>}
                              </div>
                            ))}
                            {question.type === 'DYNAMIC_FILL_IN_BLANK' && config.minGroups !== undefined && (
                              <p className="text-xs font-label text-primary-600 mt-1">
                                (可添加 {config.minGroups}~{config.maxGroups} 组)
                              </p>
                            )}
                          </div>
                        );
                      } catch {
                        return null;
                      }
                    })()}

                    {isDraft && (
                      <div className="flex items-center gap-3 mt-4">
                        <button
                          onClick={() => navigate(`/questionnaires/${id}/questions/${question.id}/edit`)}
                          className="flex items-center gap-1 text-sm font-label text-primary-600 hover:text-primary-700"
                        >
                          <EditIcon size={16} />
                          编辑
                        </button>
                        <button
                          onClick={() => setScoreModalQuestion(question)}
                          className="flex items-center gap-1 text-sm font-label text-on-surface-variant hover:text-on-surface"
                          title="只改评分，不动其他字段"
                        >
                          <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>scale</span>
                          改评分
                        </button>
                        <button
                          onClick={() => handleCopy(question.id)}
                          className="flex items-center gap-1 text-sm font-label text-on-surface-variant hover:text-on-surface"
                        >
                          <CopyIcon size={16} />
                          复制
                        </button>
                        <button
                          onClick={() => setSaveToBankModal({ show: true, questionId: question.id })}
                          className="flex items-center gap-1 text-sm font-label text-on-surface-variant hover:text-on-surface"
                        >
                          <ClipboardIcon size={16} />
                          存入题库
                        </button>
                        <button
                          onClick={() => handleDelete(question.id)}
                          className="flex items-center gap-1 text-sm font-label text-danger-600 hover:text-danger-700"
                        >
                          <TrashIcon size={16} />
                          删除
                        </button>
                        <div className="ml-auto flex items-center gap-2">
                          <button
                            onClick={() => handleMoveUp(index)}
                            disabled={index === 0}
                            className="p-1 text-on-surface-variant hover:text-on-surface disabled:opacity-30 disabled:cursor-not-allowed"
                            title="上移"
                          >
                            <ChevronUpIcon size={20} />
                          </button>
                          <button
                            onClick={() => handleMoveDown(index)}
                            disabled={index === questions.length - 1}
                            className="p-1 text-on-surface-variant hover:text-on-surface disabled:opacity-30 disabled:cursor-not-allowed"
                            title="下移"
                          >
                            <ChevronDownIcon size={20} />
                          </button>
                        </div>
                      </div>
                    )}

                    {/* 已发布问卷：只允许"改评分"，其他字段只读保护 */}
                    {!isDraft && (
                      <div className="flex items-center gap-3 mt-4">
                        <button
                          onClick={() => setScoreModalQuestion(question)}
                          className="flex items-center gap-1 text-sm font-label text-primary-600 hover:text-primary-700"
                          title="已发布问卷只能修改评分。评分修改不影响题目本身或学生答案"
                        >
                          <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>scale</span>
                          改评分
                        </button>
                        <span className="text-xs font-label text-on-surface-variant">
                          （已发布问卷只能修改评分配置）
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              </React.Fragment>
            ))}
          </div>
        </>
      )}
      </>
      )}

      {/* 跳转逻辑标签页 */}
      {activeTab === 'logic' && (
        <DisplayLogicManager
          questionnaireId={Number(id)}
          questions={questions}
          onUpdate={fetchData}
          questionnaireStatus={questionnaire?.status}
        />
      )}

      {/* 指标配置标签页 */}
      {activeTab === 'indicators' && id && (
        <div className="bg-surface-container-lowest rounded-3xl shadow-soft p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold font-headline text-on-surface">指标配置</h2>
              <p className="text-sm font-body text-on-surface-variant mt-1">配置问卷的评分指标和结论规则</p>
            </div>
            <button
              onClick={() => navigate(`/questionnaires/${id}/indicators`)}
              className="px-4 py-2 gradient-primary text-on-primary rounded-xl shadow-teal text-sm font-label font-medium"
            >
              进入配置页面
            </button>
          </div>
          <div className="bg-blue-50 rounded-2xl p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-label font-medium text-blue-800">关于指标配置</h3>
                <div className="mt-2 text-sm font-body text-blue-700">
                  <p>指标配置用于设置问卷的评分体系和结论规则，包括：</p>
                  <ul className="list-disc list-inside mt-1 space-y-1">
                    <li>创建多个评分指标（如心理健康、学业压力等）</li>
                    <li>配置每个问题选项的得分</li>
                    <li>设置评分公式（求和、平均、加权）</li>
                    <li>定义结论规则（正常、预警、严重等级别）</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 报告模板标签页 — 仅配置；查看请去统计页 */}
      {activeTab === 'report' && id && (
        <div className="bg-surface-container-lowest rounded-3xl shadow-soft p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold font-headline text-on-surface">报告模板</h2>
              <p className="text-sm font-body text-on-surface-variant mt-1">配置问卷的大报告模板（HTML + 占位符）</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate(`/questionnaires/${id}/statistics?tab=report`)}
                className="px-4 py-2 bg-surface-container-high text-on-surface rounded-xl text-sm font-label font-medium hover:bg-surface-container-low inline-flex items-center gap-1.5"
              >
                <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>description</span>
                查看报告
              </button>
              <button
                onClick={() => navigate(`/questionnaires/${id}/report`)}
                className="px-4 py-2 gradient-primary text-on-primary rounded-xl shadow-teal text-sm font-label font-medium inline-flex items-center gap-1.5"
              >
                <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>edit_note</span>
                编辑模板
              </button>
            </div>
          </div>
          <div className="bg-blue-50 rounded-2xl p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-label font-medium text-blue-800">配置 vs 查看</h3>
                <div className="mt-2 text-sm font-body text-blue-700">
                  <p><strong>编辑模板</strong>：在此页面（或独立编辑器）配置报告 HTML 内容、插入占位符、配置图表数据。</p>
                  <p className="mt-1"><strong>查看报告</strong>：在统计页面查看真实数据渲染的报告（支持按学校/班级筛选）。</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 知情同意书标签页 */}
      {activeTab === 'consent' && questionnaire && id && (
        <ConsentConfigTab
          questionnaireId={Number(id)}
          questionnaire={questionnaire}
          onUpdate={fetchData}
        />
      )}

      {/* 分页设置标签页 */}
      {activeTab === 'pagination' && questionnaire && id && (
        <PaginationConfigTab
          questionnaireId={Number(id)}
          questionnaire={questionnaire}
          questions={questions}
          onUpdate={fetchData}
        />
      )}

      {/* 版本历史标签页 */}
      {activeTab === 'versions' && questionnaire && (
        <VersionHistory
          questionnaireId={Number(id)}
          currentQuestionnaire={questionnaire}
          onRollback={fetchData}
        />
      )}

      {/* 推送管理标签页 */}
      {activeTab === 'pushes' && (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold font-headline text-on-surface">推送列表</h2>
            <button
              onClick={() => navigate(`/questionnaires/${id}/push`, { state: { openCreate: true } })}
              className="flex items-center gap-2 px-4 py-2 gradient-primary text-on-primary rounded-xl shadow-teal font-label"
            >
              <PlusIcon size={20} />
              创建推送
            </button>
          </div>

          {loadingPushes ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : pushes.length === 0 ? (
            <div className="text-center py-12 bg-surface-container-lowest rounded-3xl shadow-soft">
              <p className="text-on-surface-variant font-body mb-4">暂无推送记录</p>
              <button
                onClick={() => navigate(`/questionnaires/${id}/push`, { state: { openCreate: true } })}
                className="inline-flex items-center gap-2 px-4 py-2 gradient-primary text-on-primary rounded-xl shadow-teal font-label"
              >
                <PlusIcon size={20} />
                创建推送
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {pushes.map((push) => (
                <div
                  key={push.id}
                  onClick={() => handleViewPushDetail(push)}
                  className="bg-surface-container-lowest p-6 rounded-2xl shadow-soft hover:shadow-soft-md transition-shadow cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold font-headline text-on-surface">推送 #{push.id}</h3>
                        <span className={`px-2 py-1 rounded-xl text-xs font-label font-medium ${
                          push.status === 'ACTIVE' ? 'bg-success-100 text-success-700' : 'bg-surface-container-high text-on-surface'
                        }`}>
                          {push.statusDisplayName}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm font-body text-on-surface-variant">
                        {push.schoolIds && push.schoolIds.length > 0 && (
                          <span>学校: {push.schoolIds.length} 个</span>
                        )}
                        {push.classIds && push.classIds.length > 0 && (
                          <span>班级: {push.classIds.length} 个</span>
                        )}
                        {push.grades && push.grades.length > 0 && (
                          <span>年级: {push.grades.join(', ')}</span>
                        )}
                        {push.targetRoles && push.targetRoles.length > 0 && (
                          <span>角色: {push.targetRoles.join(', ')}</span>
                        )}
                      </div>
                      <div className="mt-2 text-sm font-label text-on-surface-variant">
                        <span>开始: {push.startTime}</span>
                        <span className="mx-2">-</span>
                        <span>结束: {push.endTime}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 标记版本确认浮窗 */}
      {showMarkVersionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-surface-container-lowest rounded-3xl p-6 w-full max-w-md shadow-soft">
            <h2 className="text-xl font-bold font-headline text-on-surface mb-4">标记新版本</h2>
            <div className="mb-6">
              <p className="text-on-surface font-body mb-3">
                {questionnaire?.version ?
                  `当前版本 ${questionnaire.version} 将被归档，系统将创建新的草稿版本供您继续编辑。` :
                  '这是首次标记版本。当前问卷将被标记为 v1 并归档，系统将创建 v2 版本供您继续编辑。'
                }
              </p>
              <div className="bg-yellow-50 rounded-2xl p-3">
                <p className="text-sm font-body text-yellow-800">
                  <strong>注意：</strong>归档后的版本无法再编辑，但可以随时查看和回滚。
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowMarkVersionModal(false)}
                className="flex-1 px-4 py-2 bg-surface-container-high text-on-surface rounded-xl hover:bg-surface-container-low font-label"
              >
                取消
              </button>
              <button
                onClick={confirmMarkVersion}
                className="flex-1 px-4 py-2 gradient-primary text-on-primary rounded-xl shadow-teal font-label"
              >
                确认标记
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 推送详情弹窗 */}
      {showPushDetailModal && selectedPush && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-surface-container-lowest rounded-3xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-soft">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold font-headline text-on-surface">推送详情</h2>
              <button
                onClick={() => setShowPushDetailModal(false)}
                className="text-on-surface-variant hover:text-on-surface-variant"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div className="border-b border-surface-container-high pb-4">
                <div className="grid grid-cols-2 gap-4 text-sm font-body">
                  <div>
                    <span className="text-on-surface-variant font-label">推送状态：</span>
                    <span className="font-medium text-on-surface">{selectedPush.statusDisplayName}</span>
                  </div>
                  <div>
                    <span className="text-on-surface-variant font-label">创建者：</span>
                    <span className="font-medium text-on-surface">{selectedPush.createdByUsername}</span>
                  </div>
                  <div>
                    <span className="text-on-surface-variant font-label">创建时间：</span>
                    <span className="font-medium text-on-surface">
                      {new Date(selectedPush.createdAt).toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <span className="text-on-surface-variant font-label">问卷标题：</span>
                    <span className="font-medium text-on-surface">{selectedPush.questionnaireTitle}</span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-medium font-headline text-on-surface mb-2">推送范围</h3>
                <div className="bg-surface-container-low p-4 rounded-2xl space-y-2 text-sm font-body">
                  <div>
                    <span className="text-on-surface-variant font-label">目标角色：</span>
                    <span className="font-medium ml-2 text-on-surface">
                      {selectedPush.targetRoles?.map((r: string) => getRoleDisplayName(r)).join(', ') || '未指定'}
                    </span>
                  </div>
                  {selectedPush.schoolNames && selectedPush.schoolNames.length > 0 && (
                    <div>
                      <span className="text-on-surface-variant font-label">学校：</span>
                      <span className="font-medium ml-2 text-on-surface">{selectedPush.schoolNames.join(', ')}</span>
                    </div>
                  )}
                  {selectedPush.academicYears && selectedPush.academicYears.length > 0 && (
                    <div>
                      <span className="text-on-surface-variant font-label">学年：</span>
                      <span className="font-medium ml-2 text-on-surface">{selectedPush.academicYears.join(', ')}</span>
                    </div>
                  )}
                  {selectedPush.grades && selectedPush.grades.length > 0 && (
                    <div>
                      <span className="text-on-surface-variant font-label">年级：</span>
                      <span className="font-medium ml-2 text-on-surface">{selectedPush.grades.join(', ')}</span>
                    </div>
                  )}
                  {selectedPush.classNames && selectedPush.classNames.length > 0 && (
                    <div>
                      <span className="text-on-surface-variant font-label">班级：</span>
                      <span className="font-medium ml-2 text-on-surface">{selectedPush.classNames.join(', ')}</span>
                    </div>
                  )}
                </div>
              </div>

              {(selectedPush.startTime || selectedPush.endTime) && (
                <div>
                  <h3 className="font-medium font-headline text-on-surface mb-2">时间范围</h3>
                  <div className="bg-surface-container-low p-4 rounded-2xl space-y-2 text-sm font-body">
                    {selectedPush.startTime && (
                      <div>
                        <span className="text-on-surface-variant font-label">开始时间：</span>
                        <span className="font-medium ml-2 text-on-surface">
                          {new Date(selectedPush.startTime).toLocaleString()}
                        </span>
                      </div>
                    )}
                    {selectedPush.endTime && (
                      <div>
                        <span className="text-on-surface-variant font-label">结束时间：</span>
                        <span className="font-medium ml-2 text-on-surface">
                          {new Date(selectedPush.endTime).toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowPushDetailModal(false)}
                className="px-4 py-2 bg-surface-container-high text-on-surface rounded-xl hover:bg-surface-container-low font-label"
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Publish Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showPublishConfirm}
        title="发布问卷"
        message="确定要发布这个问卷吗？发布后将无法修改。"
        confirmText="发布"
        cancelText="取消"
        onConfirm={confirmPublish}
        onCancel={() => setShowPublishConfirm(false)}
        type="warning"
      />

      {/* 存入题库模态框 */}
      <SaveToBankModal
        isOpen={saveToBankModal.show}
        questionnaireId={Number(id)}
        questionId={saveToBankModal.questionId || 0}
        onClose={() => setSaveToBankModal({ show: false, questionId: null })}
        onSaved={() => {
          setSaveToBankModal({ show: false, questionId: null });
          showToast('已存入题库', 'success');
        }}
      />

      {/* 从题库导入模态框 */}
      <ImportFromBankModal
        isOpen={showImportFromBank}
        questionnaireId={Number(id)}
        onClose={() => setShowImportFromBank(false)}
        onImported={(count) => {
          setShowImportFromBank(false);
          showToast(`已导入 ${count} 个问题`, 'success');
          fetchData();
        }}
      />

      {/* 改评分模态框 — 已发布问卷只能改评分，草稿问卷快捷入口 */}
      {scoreModalQuestion && (
        <ScoreEditModal
          open={!!scoreModalQuestion}
          questionnaireId={Number(id)}
          question={scoreModalQuestion}
          onClose={() => setScoreModalQuestion(null)}
          onSaved={() => {
            showToast('评分已更新', 'success');
            fetchData();
          }}
        />
      )}
    </div>
  );
};

function getRoleDisplayName(role: string): string {
  const roleMap: Record<string, string> = {
    'STUDENT': '学生',
    'TEACHER': '教师',
    'PARENT': '家长',
    'ADMIN': '管理员',
    'USER': '用户'
  };
  return roleMap[role] || role;
}

export default QuestionnaireDetailPage;
